import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getBaseBackendApiUrl } from '../config';
import { getToken } from '../service/authentication';


export const marketingApi = createApi({
  reducerPath: 'marketingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseBackendApiUrl(),
    prepareHeaders: async (headers) => {
      const token = await getToken();
      const profileToken = await getToken('doris');
      if (token) headers.set('authorization', `Bearer ${token}`);
      if (profileToken) headers.set('ProfileAuthorization', `Bearer ${profileToken}`);
      return headers;
    }
  }),
  keepUnusedDataFor: 60,
  tagTypes: ['Chain', 'Station', 'Campaign', 'SMSTemplate', 'EmailTemplate',
    'PrintTemplate', 'Image', 'CampaignTemplates'],
  endpoints: (builder) => ({
    getAllChains: builder.query({
      query: () => ({
        url: 'chains'
      }),
      providesTags: () => ['Chain']
    }),
    getStationsByChain: builder.query({
      query: (chainId) => ({
        url: `chains/${chainId}/sites`
      }),
      providesTags: () => ['Station']
    }),
    getAllCampaigns: builder.query({
      query: ({chainId, siteId}) => ({
        url: 'campaigns',
        params: { chainId, siteId }
      }),
      providesTags: () => ['Campaign']
    }),
    getCampaignTemplates: builder.query({
      query: ({ chainId, siteId }) => ({
        url: 'campaigns/templates',
        params: { chainId, siteId }
      }),
      providesTags: () => ['CampaignTemplates']
    }),
    addCampaignTemplates: builder.mutation({
      query: ({ id, payload, chainId, siteId }) => ({
        url: `campaigns/${id}/templates`,
        method: 'POST',
        body: payload,
        params: { chainId, siteId }
      }),
      invalidatesTags: () => ['CampaignTemplates']
    }),
    updateCampaignTemplates: builder.mutation({
      query: ({ id, payload, chainId, siteId }) => ({
        url: `campaigns/${id}/templates`,
        method: 'PUT',
        body: payload,
        params: { chainId, siteId }
      }),
      invalidatesTags: () => ['CampaignTemplates']
    }),



    getAllPrintTemplates: builder.query({
      query: ({ chainId, siteId }) => ({
        url: 'printtemplates',
        params: { chainId, siteId }
      }),
      providesTags: (result) => [
        { type: 'PrintTemplate', id: 'LIST'},
        ...result.map(template => ({ type: 'PrintTemplate', id: template.id }))
      ],
    }),
    getPrintTemplatesById: builder.query({
      query: (id) => ({ url: `printtemplates/${id}`}),
      providesTags: (result, error, id) => [{ type: 'PrintTemplate', id }]
    }),
    addPrintTemplate: builder.mutation({
      query: ({ payload, chainId, siteId }) => ({
        url: 'printtemplates',
        method: 'POST',
        body: payload,
        params: { chainId, siteId }
      }),
      invalidatesTags: [{ type: 'PrintTemplate', id: 'LIST' }]
    }),
    updatePrintTemplate: builder.mutation({
      query: ({ payload, chainId, siteId }) => ({
        url: `printtemplates/${payload.id}`,
        method: 'PUT',
        body: payload,
        params: { chainId, siteId }
      }),
      invalidatesTags: (id) => [{ type: 'PrintTemplate', id }]
    }),
    deletePrintTemplate: builder.mutation({
      query: (id) => ({
        url: `printtemplates/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'PrintTemplate', id }]
    }),


    getAllSMSTemplates: builder.query({
      query: ({ chainId, siteId }) => ({
        url: 'smstemplates',
        params: { chainId, siteId }
      }),
      providesTags: (result, error) => {
        if (error) return ['SMSTemplate'];
        return [
          { type: 'SMSTemplate', id: 'LIST'},
          ...result.map(template => ({ type: 'SMSTemplate', id: template.id }))
        ];
      }
    }),
    getSMSTemplatesById: builder.query({
      query: (id) => ({ url: `smstemplates/${id}`}),
      providesTags: (result, error, id) => [{ type: 'SMSTemplate', id }]
    }),
    addSMSTemplate: builder.mutation({
      query: ({ payload, chainId, siteId }) => ({
        url: 'smstemplates',
        method: 'POST',
        body: payload,
        params: { chainId, siteId }
      }),
      invalidatesTags: [{ type: 'SMSTemplate', id: 'LIST' }]
    }),
    updateSMSTemplate: builder.mutation({
      query: (payload) => ({
        url: `smstemplates/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: 'SMSTemplate', id: 'LIST' },
        { type: 'SMSTemplate', id: payload.id }
      ]
    }),
    setOngoingSMSTemplate: builder.mutation({
      query: ({ id, chainId, siteId }) => ({
        url: `smstemplates/${id}/ongoing`,
        method: 'POST',
        params: { chainId, siteId }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'SMSTemplate', id: 'LIST' },
        { type: 'SMSTemplate', id: id }
      ]
    }),
    deleteSMSTemplate: builder.mutation({
      query: (id) => ({
        url: `smstemplates/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'SMSTemplate', id }]
    }),


    getAllEmailTemplates: builder.query({
      query: ({ chainId, siteId }) => ({
        url: 'emailtemplates',
        params: { chainId, siteId }
      }),
      providesTags: (result, error) => {
        if (error) return ['EmailTemplate'];
        return [
          { type: 'EmailTemplate', id: 'LIST'},
          ...result.map(template => ({ type: 'EmailTemplate', id: template.id }))
        ];
      }
    }),
    getEmailTemplatesById: builder.query({
      query: (id) => ({ url: `emailtemplates/${id}`}),
      providesTags: (result, error, id) => [{ type: 'EmailTemplate', id }]
    }),
    addEmailTemplate: builder.mutation({
      query: ({ payload, chainId, siteId }) => ({
        url: 'emailtemplates',
        method: 'POST',
        body: payload,
        params: { chainId, siteId }
      }),
      invalidatesTags: [{ type: 'EmailTemplate', id: 'LIST' }]
    }),
    updateEmailTemplate: builder.mutation({
      query: (payload) => ({
        url: `emailtemplates/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: 'EmailTemplate', id: 'LIST' },
        { type: 'EmailTemplate', id: payload.id }
      ]
    }),
    setOngoingEmailTemplate: builder.mutation({
      query: ({ id, chainId, siteId }) => ({
        url: `emailtemplates/${id}/ongoing`,
        method: 'POST',
        params: { chainId, siteId }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EmailTemplate', id: 'LIST' },
        { type: 'EmailTemplate', id: id }
      ]
    }),
    deleteEmailTemplate: builder.mutation({
      query: (id) => ({
        url: `emailtemplates/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'EmailTemplate', id }]
    }),



    getAllImages: builder.query({
      query: ({ chainId, siteId, type, page, limit }) => ({
        url: 'imagebank',
        params: { chainId, siteId, type, page, limit  }
      }),
      providesTags: (result) => [
        { type: 'Image', id: 'LIST'},
        ...result.images.map(image => ({ type: 'Image', id: image.id }))
      ],
    }),
    getImageById: builder.query({
      query: ({id}) => ({ url: `imagebank/${id}`}),
      providesTags: (result, error, id) => [{ type: 'Image', id }]
    }),
    addImage: builder.mutation({
      query: ({ payload, type, chainId, siteId }) => ({
        url: 'imagebank',
        method: 'POST',
        body: payload,
        params: { chainId, siteId, type }
      }),
      invalidatesTags: [{ type: 'Image', id: 'LIST' }]
    }),
    deleteImage: builder.mutation({
      query: (id) => ({
        url: `imagebank/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Image', id }]
    }),
  })
});


export const {
  useGetAllChainsQuery,
  useGetStationsByChainQuery,
  useGetAllCampaignsQuery,
  useGetCampaignTemplatesQuery,
  useAddCampaignTemplatesMutation,
  useUpdateCampaignTemplatesMutation,

  useGetAllPrintTemplatesQuery,
  useGetPrintTemplatesByIdQuery,
  useAddPrintTemplateMutation,
  useUpdatePrintTemplateMutation,
  useDeletePrintTemplateMutation,

  useGetAllSMSTemplatesQuery,
  useGetSMSTemplatesByIdQuery,
  useAddSMSTemplateMutation,
  useUpdateSMSTemplateMutation,
  useSetOngoingSMSTemplateMutation,
  useDeleteSMSTemplateMutation,

  useGetAllEmailTemplatesQuery,
  useGetEmailTemplatesByIdQuery,
  useAddEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useSetOngoingEmailTemplateMutation,
  useDeleteEmailTemplateMutation,

  useGetAllImagesQuery,
  useAddImageMutation,
  useGetImageByIdQuery,
  useDeleteImageMutation
} = marketingApi;